// ... (importaciones sin cambios)
import React, { useState, useRef, useEffect, Suspense } from 'react';
import { Canvas, useThree, extend, useFrame } from '@react-three/fiber';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader';
import { OrbitControls as ThreeOrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import * as THREE from 'three';
import { getDatabase, ref, onValue, get } from "firebase/database";
import app from "../../../firebaseConfig";
import { getStorage, ref as storageRef, getDownloadURL } from "firebase/storage";
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography
} from "@mui/material";


extend({ OrbitControls: ThreeOrbitControls });


function useFirebaseColors(path, randomize = false) {
  const [colors, setColors] = useState({});

  useEffect(() => {
    const db = getDatabase(app);
    const colRef = ref(db, path);
    return onValue(colRef, (snapshot) => {
      let data = snapshot.val() || {};
      if (randomize) {
        const entries = Object.entries(data);
        for (let i = entries.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [entries[i], entries[j]] = [entries[j], entries[i]];
        }
        data = Object.fromEntries(entries);
      }
      setColors(data);
    });
  }, [path, randomize]);

  return colors;
}
const ColorSelector = ({ colorsObj, selected, onSelect, title }) => {
  const entries = Object.entries(colorsObj);
  return (
    <div style={{ margin: '10px 0' }}>
      <div style={{ fontWeight: 'bold' }}>
        {title} (Actual: {selected.name || 'ninguno'})
      </div>
      <div style={{
  display: 'flex',
  justifyContent: 'center',
  flexWrap: 'wrap',
  gap: '15px',
  maxHeight: '120px',
  overflowY: 'auto',
  padding: '4px',
  border: '1px solid #ccc',
  borderRadius: 4
}}>
        {entries.map(([name, hex]) => (
          <button
            key={name}
            onClick={() => onSelect({ name, hex: `#${hex}` })}
            title={name}
            style={{
              backgroundColor: `#${hex}`,
              width: 30,
              height: 30,
              border: selected.name === name ? '3px solid #333' : '1px solid #666',
              borderRadius: '50%',
              cursor: 'pointer'
            }}
          />
        ))}
      </div>
    </div>
  );
};
const esFondoOscuro = (hex) => {
  const c = hex.replace('#', '');
  const r = parseInt(c.substr(0, 2), 16);
  const g = parseInt(c.substr(2, 2), 16);
  const b = parseInt(c.substr(4, 2), 16);
  const luminancia = (0.299 * r + 0.587 * g + 0.114 * b);
  return luminancia < 128;
};
const ControlesOrbitales = React.forwardRef((props, ref) => {
  const { camera, gl } = useThree();
  return (
    <orbitControls ref={ref} args={[camera, gl.domElement]} enableZoom enablePan enableRotate />
  );
});


const ModeloSTL = ({ file, url, autoRotate, color, setCameraPosition, setTargetPosition }) => {
  const [geometry, setGeometry] = useState(null);
  const meshRef = useRef();
  const { camera } = useThree();

  useEffect(() => {
    const cargarDesdeURL = () => {
      const loader = new STLLoader();
      loader.load(
        url,
        (geo) => {
          geo.computeBoundingBox();
          const boundingBox = geo.boundingBox;
          const center = new THREE.Vector3();
          boundingBox.getCenter(center);
          geo.translate(-center.x, -center.y, -center.z);
          geo.computeVertexNormals();

          const size = new THREE.Vector3();
          boundingBox.getSize(size);
          const maxDim = Math.max(size.x, size.y, size.z);
          const distance = maxDim * 1.2 ;

          camera.position.set(0, 0, distance);
          camera.near = 0.1;
          camera.far = distance * 10;
          camera.updateProjectionMatrix();

          setCameraPosition([0, 0, distance]);
          setTargetPosition([0, 0, 0]);

          setGeometry(geo);
        },
        undefined,
        (error) => {
          console.error("Error al cargar el STL:", error);
          alert("No se pudo cargar el STL desde Firebase.");
        }
      );
    };

    if (url) {
      cargarDesdeURL();
    }
  }, [url, camera, setCameraPosition, setTargetPosition]);

  useFrame(() => {
    if (autoRotate && meshRef.current) {
      meshRef.current.rotation.y += 0.005;
    }
  });

  if (!geometry) return null;

  return (
    <mesh geometry={geometry} ref={meshRef} castShadow receiveShadow>
      <meshPhysicalMaterial
        color={color}
        metalness={0.1}
        roughness={0.4}
        reflectivity={0.6}
        clearcoat={0.3}
        clearcoatRoughness={0.1}
      />
    </mesh>
  );
};
const ModeloGLB = ({ url, autoRotate, color, setCameraPosition, setTargetPosition }) => {
  const [scene, setScene] = useState(null);
  const gltfRef = useRef();
  const { camera } = useThree();

  useEffect(() => {
    const loader = new GLTFLoader();
    loader.load(
      url,
      (gltf) => {
        const model = gltf.scene;
        model.traverse((child) => {
          if (child.isMesh) {
            // ✅ Si el modelo trae material, conserva su material y solo cambia el color si es necesario
            if (child.material && child.material.isMeshStandardMaterial) {
              child.material.color = new THREE.Color(color);
            } else {
              // Si no trae, se le aplica uno nuevo
              child.material = new THREE.MeshPhysicalMaterial({
                color,
                metalness: 0.1,
                roughness: 0.4,
                reflectivity: 0.6,
                clearcoat: 0.3,
                clearcoatRoughness: 0.1,
              });
            }
          }
        });
        

        const box = new THREE.Box3().setFromObject(model);
        const center = new THREE.Vector3();
        box.getCenter(center);
        model.position.sub(center); // Centrar

        const size = new THREE.Vector3();
        box.getSize(size);
        const maxDim = Math.max(size.x, size.y, size.z);
        const distance = maxDim * 1.5;

        camera.position.set(0, 0, distance);
        camera.near = 0.1;
        camera.far = distance * 10;
        camera.updateProjectionMatrix();

        setCameraPosition([0, 0, distance]);
        setTargetPosition([0, 0, 0]);

        setScene(model);
      },
      undefined,
      (error) => {
        console.error("Error al cargar el GLB:", error);
        alert("No se pudo cargar el archivo GLB.");
      }
    );
  }, [url, color, camera, setCameraPosition, setTargetPosition]);

  useFrame(() => {
    if (autoRotate && gltfRef.current) {
      gltfRef.current.rotation.y += 0.005;
    }
  });

  if (!scene) return null;

  return <primitive object={scene} ref={gltfRef} />;
};


const LuzDireccionalControlable = ({ luzRef, targetRef }) => {
  useEffect(() => {
    if (luzRef.current && targetRef.current) {
      luzRef.current.target = targetRef.current;
    }
  }, [luzRef, targetRef]);

  return (
    <>
      <directionalLight
        ref={luzRef}
        position={[5, 5, 5]}
        intensity={1.5}
        castShadow
        shadow-mapSize-width={1024}
        shadow-mapSize-height={1024}
        shadow-camera-near={1}
        shadow-camera-far={50}
        shadow-camera-left={-10}
        shadow-camera-right={10}
        shadow-camera-top={10}
        shadow-camera-bottom={-10}
      />
      <object3D ref={targetRef} position={[0, 0, 0]} />
    </>
  );
};
const StlViewer = ({ categoria, producto, linkstl }) => {
  const [archivoSTL, setArchivoSTL] = useState(null);
  const [autoRotate, setAutoRotate] = useState(true);
  const orbitRef = useRef();
  const luzRef = useRef();
  const luzTargetRef = useRef();
  const [cameraPosition, setCameraPosition] = useState([0, 0, 100]);
  const [targetPosition, setTargetPosition] = useState([0, 0, 0]);
  const [colorSeleccionado, setColorSeleccionado] = useState({ name: '', hex: '#dddddd' });
  const [fondo, setFondo] = useState({ name: '', hex: '#f0f0f0' });
  const [urlSTL, setUrlSTL] = useState(null);
  const [plasticoColors, setPlasticoColors] = useState({});

  const fondoColors = useFirebaseColors("nature/Awoo/Control/SimuladorStl/ColoresFondo");
  const modoOscuro = esFondoOscuro(fondo.hex);
  const [abrirDialogoAR, setAbrirDialogoAR] = useState(false);


  useEffect(() => {
    const db = getDatabase(app);
    const pathProducto = `nature/Awoo/Categorias/${categoria}/${producto}/Variantes/Color`;
    const pathRGB = `nature/Awoo/Control/SimuladorStl/ColoresPlastico`;

    Promise.all([
      get(ref(db, pathProducto)),
      get(ref(db, pathRGB))
    ]).then(([snapColores, snapRGB]) => {
      if (!snapColores.exists() || !snapRGB.exists()) return;
      const coloresProducto = snapColores.val();
      const coloresGlobales = snapRGB.val();

      const filtrados = {};
      Object.values(coloresProducto).forEach(item => {
        const nombre = item.Nombre;
        if (nombre && coloresGlobales[nombre]) {
          filtrados[nombre] = coloresGlobales[nombre];
        }
      });
      setPlasticoColors(filtrados);


        // ⬇️ Selección aleatoria inicial
        const colorGuardado = JSON.parse(localStorage.getItem("colorSeleccionado"));

        if (colorGuardado && filtrados[colorGuardado.name]) {
          setColorSeleccionado(colorGuardado);
        } else {
          const nombres = Object.keys(filtrados);
          if (nombres.length > 0) {
            const randomName = nombres[Math.floor(Math.random() * nombres.length)];
            const colorObj = { name: randomName, hex: `#${filtrados[randomName]}` };
            setColorSeleccionado(colorObj);
            localStorage.setItem("colorSeleccionado", JSON.stringify(colorObj));
          }
        }

        



    });
  }, [categoria, producto]);

  useEffect(() => {
    if (linkstl) {
      obtenerURLDescarga();
    }
  }, [linkstl]);  

  useEffect(() => {
    window.onColorChangeStl = (nuevoColor) => {
      setColorSeleccionado(nuevoColor);
    };
  
    return () => {
      window.onColorChangeStl = null;
    };
  }, []);

  /// Va al fondo ambiente camara
const irAVistaAR = () => {
  window.location.href = `/arviewer?modelo=${linkstl}&color=${colorSeleccionado.hex.slice(1)}`;
};
  
  const zoomIn = () => {
    if (orbitRef.current?.object) {
      orbitRef.current.object.zoom *= 1.2;
      orbitRef.current.object.updateProjectionMatrix();
    }
  };
  
  const zoomOut = () => {
    if (orbitRef.current?.object) {
      orbitRef.current.object.zoom /= 1.2;
      orbitRef.current.object.updateProjectionMatrix();
    }
  };
  
  const resetView = () => {
    if (orbitRef.current) {
      orbitRef.current.object.position.set(...cameraPosition);
      orbitRef.current.target.set(...targetPosition);
      orbitRef.current.update();
    }
  };
  
  const moverLuz = (dx, dy, dz) => {
    if (luzRef.current) {
      const pos = luzRef.current.position;
      luzRef.current.position.set(pos.x + dx, pos.y + dy, pos.z + dz);
    }
  };
  

  const obtenerURLDescarga = async () => {
    try {
      const storage = getStorage(app);
      const archivoRef = storageRef(storage, linkstl);
      const url = await getDownloadURL(archivoRef);
      setUrlSTL(url);
      setArchivoSTL(null);
    } catch (error) {
      alert("No se pudo cargar el archivo desde Firebase.");
    }
  };

  return (
    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '20px', borderRadius: '10px', overflow: 'hidden' }}>
      <div style={{
        width: '99%',
        height: '400px',
        position: 'relative',
        border: '3px solid #cecece',
        borderRadius: '30px',
        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)'
      }}>
  
{/* 📷 Botón circular superior izquierdo */}
<div style={{
  position: 'absolute',
  top: 10,
  left: 10,
  zIndex: 10
}}>
  <button
    onClick={() => setAbrirDialogoAR(true)}
    style={{
      width: '45px',
      height: '45px',
      borderRadius: '50%',
      backgroundColor: '#ffffffee',
      border: '3px solid #ccc',
      fontSize: '20px',
      boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
      cursor: 'pointer'
    }}
    title="Ver en tu ambiente"
  >
    📷
  </button>
</div>

<Dialog
  open={abrirDialogoAR}
  onClose={() => setAbrirDialogoAR(false)}
  PaperProps={{
    style: {
      borderRadius: '16px',
      backgroundColor: '#fff8f0',
      border: '3px solid #ffcc80'
    }
  }}
>
  <DialogTitle
    style={{
      backgroundColor: '#ffe0b2',
      fontWeight: 'bold',
      fontSize: '20px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      padding: '16px 24px'
    }}
  >
    🌍 Simulación en Ambiente Real
  </DialogTitle>

  <DialogContent style={{ padding: '20px' }}>
    <Typography variant="body1" style={{ fontSize: '16px', color: '#5d4037' }}>
      ¿Quieres ver tu figura en el mundo real? 🧡<br />
      Activaremos tu cámara para mostrar el modelo sobre tu fondo. 📷✨
    </Typography>
  </DialogContent>

  <DialogActions style={{ justifyContent: 'space-between', padding: '12px 24px' }}>
    <Button
      onClick={() => setAbrirDialogoAR(false)}
      variant="outlined"
      color="error"
      style={{ fontWeight: 'bold' }}
    >
      ❌ Luego
    </Button>
    <Button
      onClick={irAVistaAR}
      variant="contained"
      style={{ backgroundColor: '#ff9800', color: '#fff', fontWeight: 'bold' }}
    >
      ✅ Sí quiero
    </Button>
  </DialogActions>
</Dialog>


  
        {/* Controles superiores derechos */}
        <div style={{
          position: 'absolute',
          top: 10,
          right: 10,
          zIndex: 10,
          display: 'flex',
          flexDirection: 'column',
          gap: 8
        }}>
          <button onClick={zoomIn}>🔍 Zoom +</button>
          <button onClick={zoomOut}>🔎 Zoom -</button>
          <button onClick={() => setAutoRotate(!autoRotate)}>
            {autoRotate ? '⏸️ Detener Rotación' : '🔁 Rotar Automático'}
          </button>
        </div>
  
        {/* Controles de luz */}
        <div style={{
          position: 'absolute',
          bottom: 100,
          right: 20,
          zIndex: 10,
          display: 'flex',
          flexDirection: 'column',
          gap: 5
        }}>
          <span style={{ fontWeight: 'bold' }}>Luz:</span>
          <button onClick={() => moverLuz(-1, 0, 0)}>← Izquierda</button>
          <button onClick={() => moverLuz(1, 0, 0)}>→ Derecha</button>
          <button onClick={() => moverLuz(0, 1, 0)}>↑ Arriba</button>
          <button onClick={() => moverLuz(0, -1, 0)}>↓ Abajo</button>
        </div>
  
        <Canvas style={{
          width: '100%',
          height: '100%',
          borderRadius: '30px',
          backgroundColor: fondo.hex,
          color: modoOscuro ? 'white' : 'black'
        }} shadows camera={{ position: [0, 0, 100], fov: 60 }}>
          <ambientLight intensity={0.3} />
          <hemisphereLight skyColor={0xffffff} groundColor={0x444444} intensity={0.6} />
          <LuzDireccionalControlable luzRef={luzRef} targetRef={luzTargetRef} />
          <Suspense fallback={null}>
  {archivoSTL && (
    <ModeloSTL
      file={archivoSTL}
      autoRotate={autoRotate}
      color={colorSeleccionado.hex}
      setCameraPosition={setCameraPosition}
      setTargetPosition={setTargetPosition}
    />
  )}
  {urlSTL && (
    linkstl.toLowerCase().endsWith('.stl') ? (
      <ModeloSTL
        url={urlSTL}
        autoRotate={autoRotate}
        color={colorSeleccionado.hex}
        setCameraPosition={setCameraPosition}
        setTargetPosition={setTargetPosition}
      />
    ) : linkstl.toLowerCase().endsWith('.glb') ? (
      <ModeloGLB
        url={urlSTL}
        autoRotate={autoRotate}
        color={colorSeleccionado.hex}
        setCameraPosition={setCameraPosition}
        setTargetPosition={setTargetPosition}
      />
    ) : null
  )}
</Suspense>

          <ControlesOrbitales ref={orbitRef} />
        </Canvas>
      </div>
  
      {/* Selección de color y fondo */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '20px',
        justifyContent: 'space-between',
        padding: '10px 20px'
      }}>
        <div style={{ flex: '1 1 300px', marginTop:"-25px" }}>
          <ColorSelector
            colorsObj={plasticoColors}
            selected={colorSeleccionado}
            onSelect={(nuevoColor) => {
              setColorSeleccionado(nuevoColor);
              localStorage.setItem("colorSeleccionado", JSON.stringify(nuevoColor));
              window.dispatchEvent(new Event("color-stl-cambiado"));
              if (typeof window.onColorChangeStl === 'function') {
                window.onColorChangeStl(nuevoColor);
              }
            }}
            title="🎨 Colores para Figura"
          />
        </div>
        <div style={{ flex: '1 1 250px', marginTop:"-25px" }}>
          <ColorSelector
            colorsObj={fondoColors}
            selected={fondo}
            onSelect={setFondo}
            title="🖼️ Fondo"
          />
        </div>
      </div>
    </div>
  );
  
};

export default StlViewer;

